import { getAppPermissions } from 'homey-lib/webpack/index.js';
import { useId, useState } from 'react';
import { useFocusRing, useLocale } from 'react-aria';

import { StateRoute } from '../../../lib/StateRoute';
import { ToastManager } from '../../../ToastManager';

import { AppUtils } from '../../../store/apps/AppUtils';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useApp } from '../../../store/apps/useApps.ts';
import { useApi } from '../../../store/SelectedHomeyStore.hooks.ts';

import { glyph } from '../../../theme/glyph';
import { theme } from '../../../theme/theme';
import styles from './AppSettingsDialogUpdate.module.css';

import { IconButton } from '../../../components/buttons/IconButton';
import { SolidButton } from '../../../components/buttons/SolidButton';
import { Glyph } from '../../../components/common/Glyph';
import { HomeyIcon } from '../../../components/common/HomeyIcon';
import { RoundIconWrapper } from '../../../components/common/RoundIconWrapper';
import { SpinnerFade } from '../../../components/common/SpinnerFade';
import { Heading3Medium } from '../../../components/common/text/Heading3Medium';
import { Dialog } from '../../../components/overlay/Dialog';

import {
  getAppUpdateAvailable,
  getAppUpdateInstallChannel,
  isAppUpdateEligible,
} from './AppSettingsUpdate';

const permissionsById = getAppPermissions();

export function AppSettingsDialogUpdate() {
  const { i18n } = useI18n();
  const { locale } = useLocale();
  const { context } = AppSettingsDialogUpdate.route.useContext();
  const { api } = useApi();
  const { app } = useApp({ appId: context?.appId });

  const [isUpdating, setIsUpdating] = useState(false);
  const permissionListTitleId = useId();
  const permissionListFocusRing = useFocusRing();

  const appBackgroundColor = AppUtils.getBackgroundColor(app ?? undefined);
  const updateAvailable = getAppUpdateAvailable(app);
  const updateVersion = updateAvailable?.version;
  const addedPermissions = getAddedPermissions(app, updateAvailable);
  const isUpdateEligible = isAppUpdateEligible({ api, app });
  const title =
    app == null
      ? i18n.messageFormatter('common.update')
      : i18n.messageFormatter('settings.app.updateTitle', { name: app.name });

  function handleClosePress() {
    if (isUpdating === true) {
      return;
    }

    AppSettingsDialogUpdate.route.close();
  }

  async function handleUpdatePress() {
    if (api == null || app == null || isUpdateEligible === false || isUpdating === true) {
      return;
    }

    if (updateVersion == null) {
      return;
    }

    setIsUpdating(true);

    try {
      await api.apps.installFromAppStore({
        id: app.id,
        channel: getAppUpdateInstallChannel(app.channel),
        version: updateVersion,
      });

      AppSettingsDialogUpdate.route.close();
    } catch (error) {
      ToastManager.handleError(error);
      setIsUpdating(false);
    }
  }

  return (
    <Dialog aria-label={title} onClose={handleClosePress}>
      <div className={styles.root}>
        <div className={styles.closeButtonWrapper}>
          <IconButton
            aria-label={i18n.messageFormatter('common.close')}
            isDisabled={isUpdating}
            url={glyph.core.cross}
            color={theme.color.icon_light}
            size={theme.icon.size_default}
            onPress={handleClosePress}
          />
        </div>

        <div className={styles.header}>
          <RoundIconWrapper size="var(--module-app-icon-wrapper-size)" color={appBackgroundColor}>
            <HomeyIcon
              size="var(--module-app-icon-size)"
              iconObj={app?.iconObj}
              color={theme.color.white}
            />
          </RoundIconWrapper>

          <div className={styles.heading}>
            <Heading3Medium className={styles.title}>{title}</Heading3Medium>

            {updateVersion != null && (
              <p className={styles.version}>
                v{app?.version} → v{updateVersion}
              </p>
            )}
          </div>
        </div>

        {addedPermissions.length > 0 && (
          <>
            <p id={permissionListTitleId} className={styles.permissionListTitle}>
              {i18n.messageFormatter('settings.app.updatePermissionsTitle')}
            </p>
            <ul
              {...permissionListFocusRing.focusProps}
              className={styles.permissionList}
              aria-labelledby={permissionListTitleId}
              tabIndex={0}
              data-is-focus-visible={permissionListFocusRing.isFocusVisible}
            >
              {addedPermissions.map((permissionId) => {
                return (
                  <li className={styles.permission} key={permissionId}>
                    <Glyph
                      source={glyph.core.lock}
                      color={theme.color.icon_light}
                      size={theme.icon.size_small}
                    />
                    <span>{getPermissionTitle(permissionId, locale)}</span>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <div className={styles.actions}>
          <SolidButton
            autoFocus={true}
            isDisabled={isUpdateEligible === false || isUpdating === true}
            styleFlat={true}
            styleColor="blue"
            onPress={handleUpdatePress}
          >
            <SpinnerFade
              isActive={isUpdating}
              color={theme.color.white}
              size={theme.icon.size_small}
            />
            <SolidButton.Text>
              {isUpdating
                ? i18n.messageFormatter('settings.app.updating')
                : i18n.messageFormatter('common.update')}
            </SolidButton.Text>
          </SolidButton>
        </div>
      </div>
    </Dialog>
  );
}

AppSettingsDialogUpdate.route = new StateRoute({ key: AppSettingsDialogUpdate.name });

function getPermissionTitle(permissionId: string, locale: string): string {
  const title = permissionsById[permissionId]?.title;

  if (isRecord(title)) {
    const localizedTitle = title[locale] ?? title.en;

    if (typeof localizedTitle === 'string') {
      return localizedTitle;
    }
  }

  return permissionId;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value != null;
}

function getAddedPermissions(
  app: { permissions: unknown[] } | null | undefined,
  updateAvailable: ReturnType<typeof getAppUpdateAvailable>,
): string[] {
  if (app == null || updateAvailable == null) {
    return [];
  }

  return updateAvailable.permissions.filter((permissionId) => {
    return app.permissions.includes(permissionId) === false;
  });
}
